import {
  useDeleteOtherAssessmentsMediaMutation,
  useUpdateOtherAssessmentsMediaMutation,
} from "app/services/api/residentsApi";
import { useResidentContext } from "../provider/ResidentProvider";
import { useState } from "react";
import { useDisplayToast } from "ui/hooks/useDisplayToast";
import { useForm } from "react-hook-form";
import { convertToFile } from "ui/utils/fileUtils";
import { handleBackendError } from "ui/utils/Errors";
import { usePageFocus } from "ui/utils/FocusHook";
import { AssessmentsFormModel } from "app/services/api/types/residents/models/AssessmentFormModel";
import { DocumentPickerAsset } from "expo-document-picker";
import { isDocumentPickerAsset, isMediaModel } from "ui/types/ThrelsFileType";

export const useHandleResidentAssessments = () => {
  const { residentId, resident } = useResidentContext();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { showNewToast, showErrorToast } = useDisplayToast();

  const [triggerUpdateOtherAssessmentsMedia] =
    useUpdateOtherAssessmentsMediaMutation();
  const [triggerDeleteOtherMedia] = useDeleteOtherAssessmentsMediaMutation();

  const {
    control,
    reset,
    handleSubmit,
    setError,
    formState: { isSubmitting },
  } = useForm<AssessmentsFormModel>({
    defaultValues: {
      other_assessments_medias: [],
    },
  });

  usePageFocus(() => {
    if (resident) {
      reset({
        other_assessments_medias: resident.other_assessments_medias || [],
      });
    }
  }, [resident, reset]);

  const updateOtherAssessments = async (
    otherAssessmentsMedias: DocumentPickerAsset[],
    residentId: number,
  ) => {
    const formData = new FormData();
    otherAssessmentsMedias.forEach((file) => {
      const convertedFiles: File[] = convertToFile([file]);
      convertedFiles.forEach((convertedFile) => {
        formData.append("other_assessments_medias[]", convertedFile);
      });
    });

    await triggerUpdateOtherAssessmentsMedia({
      residentId,
      body: formData,
    }).unwrap();
  };

  const submitForm = () => {
    return new Promise<boolean>((resolve) => {
      if (isSubmitting) {
        resolve(false);
        return;
      }
      handleSubmit(
        async ({ other_assessments_medias }) => {
          try {
            if (!residentId) {
              resolve(false);
              return;
            }

            const otherAssessmentsMedia = other_assessments_medias?.filter(
              (asset) => isDocumentPickerAsset(asset),
            );
            const otherDeletedMedia =
              resident?.other_assessments_medias?.filter(
                (media) =>
                  !other_assessments_medias?.some(
                    (item) => isMediaModel(item) && item.id === media.id,
                  ),
              );

            if (otherDeletedMedia?.length) {
              await Promise.all(
                otherDeletedMedia.map((media) =>
                  triggerDeleteOtherMedia({
                    residentId,
                    mediaId: media.id,
                  }).unwrap(),
                ),
              );
            }

            if (otherAssessmentsMedia?.length) {
              await updateOtherAssessments(otherAssessmentsMedia, residentId);
            }

            showNewToast({
              title: "Assessment Updated",
              message: "Successfully updated resident's assessment.",
              type: "success",
            });
            resolve(true);
          } catch (e) {
            const result = handleBackendError(e, setError, setErrorMessage);
            showErrorToast(e);
            resolve(result);
          }
        },
        (errors) => {
          console.error("Form errors:", errors);
          resolve(false);
        },
      )();
    });
  };

  return {
    control,
    errorMessage,
    submitForm,
  };
};
